import { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { Image as ImageIcon, Loader2, RefreshCw, Edit2, Library } from 'lucide-react'
import { Button } from '@lovable/components/ui/button'
import { Textarea } from '@lovable/components/ui/textarea'
import { CreditCounter } from '@lovable/components/CreditCounter'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@lovable/components/ui/tabs'
import { GameBoardPicker } from '@/pages/GameEditor/components/GameBoardsPicker/GameBoardsPicker'
import { DEFAULT_GAME_BOARD_SETTINGS } from '@/pages/GameEditor/types'
import { toast } from 'sonner'
import { t } from 'i18next'

interface Props {
  onRequestGameBoard: (prompt: string, aspectRatio: string) => void
  currentGameBoardUrl?: string
  firstTaskPrompt?: string
  onGameBoardReady: () => void
  onSelectBoard: (boardUrl: string) => void
  onCancelGeneration: () => void
  errorStatus: string | null
}

export const Board: React.FC<Props> = ({
  onRequestGameBoard,
  currentGameBoardUrl,
  firstTaskPrompt,
  onGameBoardReady,
  onSelectBoard,
  onCancelGeneration,
  errorStatus,
}: Props) => {
  const location = useLocation()
  const [hasStarted, setHasStarted] = useState(location.state?.viewBoard || false)
  const [initialPrompt, setInitialPrompt] = useState('')
  const [showDefaultPrompt, setShowDefaultPrompt] = useState(true)
  const [editingPrompt, setEditingPrompt] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null)
  const [aspectRatio, setAspectRatio] = useState<'square' | 'landscape' | 'portrait'>('square')
  const [selectedLibraryBoard, setSelectedLibraryBoard] = useState<string | null>(null)

  const inspiringPrompts = [
    'A vibrant underwater world with coral reefs, colorful sea creatures, and sunken treasure chests along a winding path',
    'A futuristic space station with neon pathways, holographic elements, and floating platforms among the stars',
    'An enchanted forest with glowing mushrooms, magical creatures, and ancient trees forming a mystical trail',
    'A steampunk city with gears, brass machinery, Victorian architecture, and steam-powered vehicles on cobblestone streets',
    'A candy land paradise with lollipop trees, chocolate rivers, and gummy bear forests creating a sweet adventure path',
    'An ancient Egyptian temple with hieroglyphics, golden artifacts, and mysterious chambers lit by torches',
    'A cyberpunk metropolis with towering skyscrapers, neon signs, flying cars, and rain-soaked streets',
    'A medieval castle kingdom with stone walls, banners, dragons, and knights guarding the royal path',
    'A tropical jungle adventure with exotic plants, hidden temples, waterfalls, and wild animals along vine-covered trails',
    'A winter wonderland with ice castles, aurora borealis, snow-covered mountains, and friendly polar creatures',
  ]
  const handleInspireMe = () => {
    const randomPrompt = inspiringPrompts[Math.floor(Math.random() * inspiringPrompts.length)]
    setInitialPrompt(firstTaskPrompt ?? randomPrompt)
  }

  useEffect(() => {
    if (errorStatus) {
      toast.error(errorStatus)
    }
  }, [errorStatus])

  useEffect(() => {
    if (currentGameBoardUrl) {
      console.log('stop polling')
      onGameBoardReady()
      setGenerating(false)
      if (!currentGameBoardUrl.toLocaleLowerCase().includes('error')) {
        setGeneratedImageUrl(currentGameBoardUrl)
      } else if (currentGameBoardUrl.includes('revised_prompt')) {
        // const promptStart = currentGameBoardUrl.indexOf('revised_prompt') + 17
        // const revisedPrompt = currentGameBoardUrl.substring(promptStart, currentGameBoardUrl.indexOf('}', promptStart) - 4)
        // setInitialPrompt(revisedPrompt)
        toast.error(
          t(
            'engine.board.general_image_error',
            'The image generation failed, probably due to content policay violation. Please try to revise the prompt.',
          ),
        )
      }
    }
  }, [currentGameBoardUrl, onGameBoardReady])

  const handleStartGeneration = useCallback(() => {
    if (selectedLibraryBoard) {
      onSelectBoard(selectedLibraryBoard)
      return
    }
    if (!initialPrompt.trim()) return
    onRequestGameBoard(initialPrompt, aspectRatio)
    setHasStarted(true)
    setGenerating(true)
    setEditingPrompt(false)
  }, [aspectRatio, initialPrompt, onRequestGameBoard, onSelectBoard, selectedLibraryBoard])

  const handleRegenerateFromPrompt = useCallback(() => {
    if (!initialPrompt.trim()) return
    onRequestGameBoard(initialPrompt, aspectRatio)
    setGenerating(true)
    setEditingPrompt(false)
  }, [aspectRatio, initialPrompt, onRequestGameBoard])

  const handleImageError = () => {
    setGeneratedImageUrl(null)
    setSelectedLibraryBoard(null)
    toast.error(
      t(
        'engine.board.general_image_error',
        'The image generation failed, probably due to content policay violation. Please try to revise the prompt.',
      ),
    )
  }

  const onCancelGenerationInternal = useCallback(() => {
    setGenerating(false)
    onCancelGeneration()
    toast.error(t('engine.board.cancelled_toast', 'The image generation cancelled. Please try to revise the prompt.'))
  }, [onCancelGeneration])

  return (
    <div className='min-h-screen bg-gradient-to-b from-background via-background to-muted/20'>
      {/* Credit Counter */}
      <div className='border-b border-border/50 bg-card/80 backdrop-blur-md sticky top-0 z-10 shadow-sm'>
        <div className='container max-w-7xl mx-auto px-6 lg:px-8 py-5 flex justify-end'>
          <CreditCounter />
        </div>
      </div>

      <div className='container max-w-7xl mx-auto px-6 lg:px-8 py-12 animate-fade-in'>
        {/* Header */}
        <div className='mb-12 text-center max-w-3xl mx-auto'>
          <h1 className='text-5xl font-bold text-foreground mb-4 tracking-tight'>
            {t('engine.board.main_title', 'Game Board Design')}
          </h1>
        </div>

        {/* Initial Prompt Input */}
        {!hasStarted && (
          <div className='max-w-5xl mx-auto'>
            <div className='bg-gradient-to-br from-card via-card to-card/80 border border-border/60 rounded-2xl p-8 shadow-2xl backdrop-blur-sm hover:shadow-3xl transition-shadow duration-300'>
              <div className='flex items-start gap-4 mb-6'>
                <div className='w-14 h-14 rounded-xl bg-gradient-to-br from-primary via-primary-glow to-secondary flex items-center justify-center shadow-lg flex-shrink-0'>
                  <ImageIcon className='w-7 h-7 text-primary-foreground' />
                </div>
                <div className='flex-1'>
                  <h2 className='text-2xl font-bold text-foreground mb-2'>
                    {t('engine.board.create_title', 'Create Your Game Board')}
                  </h2>
                  <p className='text-muted-foreground'>
                    {t('engine.board.create_subtitle', 'Choose from our library or describe your own vision')}
                  </p>
                </div>
              </div>

              <Tabs defaultValue='custom' className='mb-6'>
                <TabsList className='grid w-full grid-cols-2 mb-6'>
                  <TabsTrigger value='custom' className='flex items-center gap-2'>
                    <Edit2 className='w-4 h-4' />
                    {t('engine.board.create_custom', 'Create Custom')}
                  </TabsTrigger>
                  <TabsTrigger value='library' className='flex items-center gap-2'>
                    <Library className='w-4 h-4' />
                    {t('engine.board.choose_library', 'Choose from Library')}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value='custom' className='space-y-6'>
                  <div className='relative'>
                    <Textarea
                      defaultValue={t(
                        'engine.board.create_example_placeholder',
                        "Example: 'A vibrant underwater world with coral reefs and sea creatures' or 'A futuristic space station with neon pathways and holographic elements'...",
                      )}
                      value={
                        showDefaultPrompt
                          ? t(
                              'engine.board.create_example_placeholder',
                              "Example: 'A vibrant underwater world with coral reefs and sea creatures' or 'A futuristic space station with neon pathways and holographic elements'...",
                            )
                          : initialPrompt
                      }
                      onChange={(e) => {
                        setShowDefaultPrompt(false)
                        setInitialPrompt(e.target.value)
                        setSelectedLibraryBoard(null)
                      }}
                      className='min-h-[140px] text-base resize-none focus:ring-2 focus:ring-primary/50 transition-all'
                    />
                    <div className='flex justify-end mt-2 gap-2'>
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        onClick={() => {
                          setShowDefaultPrompt(false)
                          handleInspireMe()
                        }}
                        className='text-xs'
                        //className='absolute bottom-3 right-3 text-xs'
                        //className='absolute bottom-3 right-3 text-xs hover:bg-primary/10 transition-colors'
                      >
                        ✨ {t('engine.gameboard_usemycontent', 'Use My Game Content')}
                      </Button>
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        onClick={() => {
                          setShowDefaultPrompt(true)
                          setInitialPrompt('')
                        }}
                        className='text-xs'
                        //className='absolute bottom-3 right-3 text-xs hover:bg-primary/10 transition-colors'
                      >
                        {t('engine.gameboard_show_exampleprompt', 'Show example prompt')}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <label className='text-sm font-medium text-foreground mb-3 block'>
                      {t('engine.board.board_layout', 'Board layout')}
                    </label>
                    <div className='grid grid-cols-3 gap-3'>
                      {[
                        {
                          value: 'square',
                          label: t('engine.board.board_square', 'Square'),
                          icon: '⬜',
                        },
                        {
                          value: 'landscape',
                          label: t('engine.board.board_landscape', 'Landscape'),
                          icon: '▭',
                        },
                        {
                          value: 'portrait',
                          label: t('engine.board.board_portrait', 'Portrait'),
                          icon: '▯',
                        },
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setAspectRatio(option.value as typeof aspectRatio)}
                          className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-200 ${
                            aspectRatio === option.value
                              ? 'border-primary bg-primary/10 shadow-md'
                              : 'border-border/60 bg-card/50 hover:border-primary/50 hover:bg-card'
                          }`}
                        >
                          <span className='text-2xl mb-2'>{option.icon}</span>
                          <span className='text-sm font-medium text-foreground'>{option.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value='library' className='space-y-4'>
                  <div className='relative group'>
                    <GameBoardPicker
                      initialValues={DEFAULT_GAME_BOARD_SETTINGS}
                      onChange={(evt) => {
                        if (evt.gameBoards[0]) setSelectedLibraryBoard(evt.gameBoards[0].url)
                      }}
                      singleStatic
                    />
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            <div className='flex flex-col sm:flex-row gap-4 mt-6'>
              <Button
                onClick={handleStartGeneration}
                disabled={!initialPrompt.trim() && !selectedLibraryBoard}
                className='flex-1 h-14 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-r from-primary via-primary-glow to-secondary hover:opacity-90'
                size='lg'
              >
                <ImageIcon className='w-5 h-5 mr-3' />
                {t('engine.board.generate_board', 'Generate My Game Board')}
              </Button>
            </div>
          </div>
        )}

        {hasStarted && (
          <div className={`grid grid-cols-1 gap-8 ${selectedLibraryBoard ? 'lg:grid-cols-1' : 'lg:grid-cols-5'}`}>
            {/* Left Column - Prompt Card (hidden when library board selected) */}
            {!selectedLibraryBoard && (
              <div className='lg:col-span-2 space-y-6'>
                <div className='bg-gradient-to-br from-card via-card to-card/90 border border-border/60 rounded-2xl p-6 shadow-xl backdrop-blur-sm sticky top-32'>
                  <div className='flex items-center justify-between mb-5'>
                    <div className='flex items-center gap-3'>
                      <div className='w-11 h-11 rounded-xl bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center shadow-md'>
                        <ImageIcon className='w-6 h-6 text-primary-foreground' />
                      </div>
                      <h3 className='text-xl font-bold text-foreground'>
                        {t('engine.board.your_prompt', 'Your Prompt')}
                      </h3>
                    </div>
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => setEditingPrompt(!editingPrompt)}
                      className='hover:bg-primary/10 transition-colors'
                    >
                      <Edit2 className='w-4 h-4' />
                    </Button>
                  </div>

                  {editingPrompt ? (
                    <div className='space-y-4'>
                      <Textarea
                        value={initialPrompt}
                        onChange={(e) => setInitialPrompt(e.target.value)}
                        className='min-h-[120px] text-base resize-none focus:ring-2 focus:ring-primary/50'
                      />

                      <div>
                        <label className='text-sm font-medium text-foreground mb-2 block'>
                          {t('engine.board.board_layout', 'Board layout')}
                        </label>
                        <div className='grid grid-cols-3 gap-2'>
                          {[
                            {
                              value: 'square',
                              label: t('engine.board.board_square', 'Square'),
                              icon: '⬜',
                            },
                            {
                              value: 'landscape',
                              label: t('engine.board.board_landscape', 'Landscape'),
                              icon: '▭',
                            },
                            {
                              value: 'portrait',
                              label: t('engine.board.board_portrait', 'Portrait'),
                              icon: '▯',
                            },
                          ].map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setAspectRatio(option.value as typeof aspectRatio)}
                              className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all duration-200 ${
                                aspectRatio === option.value
                                  ? 'border-primary bg-primary/10'
                                  : 'border-border/60 bg-card/50 hover:border-primary/50'
                              }`}
                            >
                              <span className='text-xl mb-1'>{option.icon}</span>
                              <span className='text-xs font-medium text-foreground'>{option.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className='flex gap-3'>
                        <Button
                          variant='outline'
                          onClick={handleRegenerateFromPrompt}
                          disabled={!initialPrompt.trim() || generating}
                          className='flex-1 shadow-md hover:shadow-lg hover:bg-primary transition-all'
                        >
                          <RefreshCw className='w-4 h-4 mr-2' />
                          {t('engine.board.regenerate', 'Regenerate')}
                        </Button>
                        <Button variant='outline' onClick={() => setEditingPrompt(false)} className='w-full sm:w-auto'>
                          {t('engine.board.cancel', 'Cancel')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className='space-y-4'>
                      <div>
                        <p className='text-sm text-muted-foreground leading-relaxed bg-muted/30 rounded-lg p-4 border border-border/40 mb-3'>
                          {initialPrompt}
                        </p>
                        <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                          <span className='font-medium'>{t('engine.gameboard_size', 'Size:')}</span>
                          <span className='capitalize bg-muted/50 px-2 py-1 rounded'>{aspectRatio}</span>
                        </div>
                      </div>
                      <Button
                        variant='outline'
                        onClick={handleRegenerateFromPrompt}
                        disabled={generating}
                        className='w-full hover:bg-primary transition-colors'
                      >
                        <RefreshCw className='w-4 h-4 mr-2' />
                        {t('engine.board.gen_alt', 'Generate Alternative Design')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Right Column - Board Preview */}
            <div
              className={`space-y-6 ${
                selectedLibraryBoard ? 'lg:col-span-1 max-w-5xl mx-auto w-full' : 'lg:col-span-3'
              }`}
            >
              <div className='bg-gradient-to-br from-card via-card to-card/90 border border-border/60 rounded-2xl p-8 shadow-2xl backdrop-blur-sm'>
                <div className='flex items-center justify-between mb-8'>
                  <div>
                    <h2 className='text-2xl font-bold text-foreground mb-1'>
                      {t('engine.board.your_board', 'Your Game Board')}
                    </h2>
                  </div>
                </div>

                {generating ? (
                  <div className='aspect-[4/3] bg-gradient-to-br from-muted/50 to-muted rounded-xl flex items-center justify-center backdrop-blur-sm border border-border/40'>
                    <div className='text-center space-y-6'>
                      <div className='relative'>
                        <Loader2 className='w-16 h-16 text-primary animate-spin mx-auto' />
                        <div className='absolute inset-0 blur-xl bg-primary/20 animate-pulse' />
                      </div>
                      <div className='space-y-2'>
                        <p className='text-base font-medium text-foreground'>
                          {t('engine.board.crafting', 'Crafting your board')}
                        </p>
                        <p className='text-sm text-muted-foreground'>
                          {t('engine.board.take_a_moment', 'This may take a moment...')}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className='space-y-4'>
                    <div className='aspect-[4/3] bg-gradient-to-br from-background via-muted/30 to-muted/60 rounded-xl border-2 border-border/60 overflow-hidden shadow-inner group hover:shadow-xl transition-shadow duration-300'>
                      {selectedLibraryBoard ? (
                        <img
                          src={selectedLibraryBoard}
                          alt='Selected game board'
                          className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-500'
                          onError={handleImageError}
                        />
                      ) : generatedImageUrl ? (
                        <img
                          src={generatedImageUrl}
                          alt='Generated game board'
                          className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-500'
                          onError={handleImageError}
                        />
                      ) : (
                        <div className='text-center items-center mt-3'>
                          <p className='text-sm text-muted-foreground'>
                            {t(
                              'engine.board.image_error',
                              'Could not generate your image. Please edit your prompt and try again',
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {generating && (
                  <div className='flex justify-center' style={{ marginTop: '20px' }}>
                    <Button variant='outline' onClick={() => onCancelGenerationInternal()} className='w-full sm:w-auto'>
                      {t('engine.goals_tasks.cancel_button', 'Cancel')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {hasStarted && (
          <div className='mt-12 max-w-2xl mx-auto'>
            <div className='flex flex-col sm:flex-row gap-4'>
              <Button
                variant='outline'
                onClick={() => {
                  setHasStarted(false)
                  setSelectedLibraryBoard(null)
                  setGeneratedImageUrl(null)
                }}
                className='w-full h-12 w-auto'
                //className='flex-1 h-12 text-base hover:bg-muted'
              >
                {selectedLibraryBoard
                  ? t('engine.gameboard_change_library_board', 'Change My Game Board')
                  : generatedImageUrl
                  ? t('engine.gameboard_change_settings', 'Change My Game Board Settings')
                  : t('engine.gameboard_selection', 'Change Selection')}
              </Button>
              <Button
                onClick={() => onSelectBoard(selectedLibraryBoard ?? generatedImageUrl ?? '')}
                disabled={generating}
                className='flex-1 h-12 text-base font-semibold shadow-lg hover:shadow-xl transition-all bg-gradient-to-r from-primary via-primary-glow to-secondary hover:opacity-90'
              >
                {t('engine.board.proceed', 'Proceed to Publish')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
